'use client';

import { Button, Icon } from '@repo/ui';
import ArReportingReload from '../common/ArReportingReload';

interface WriteOffTabHeaderProps {
  /** Title displayed at the top of the section */
  title: string;
  /** Description text below the title */
  description: string;
  /** Loading state for the refresh button */
  isLoading: boolean;
  /** Callback when refresh button is clicked */
  onRefresh: () => void;
  /** Number of selected rows for bulk actions */
  selectedCount?: number;
  /** Callback when Print PDF button is clicked */
  onPrintPdf?: () => void;
  /** Whether a PDF print is currently in progress */
  isPrinting?: boolean;
  /** Render prop for toolbar - allows parent to inject custom toolbar */
  renderToolbar: () => React.ReactNode;
}

/**
 * Header component for Write-Off main list views (No Grouping / Group By Client).
 * Displays title, description, refresh button, and an injected toolbar.
 */
export function WriteOffTabHeader({
  title,
  description,
  isLoading,
  onRefresh,
  selectedCount = 0,
  onPrintPdf,
  isPrinting = false,
  renderToolbar,
}: WriteOffTabHeaderProps) {
  return (
    <div className="flex flex-col gap-4">
      {/* Title Row */}
      <div className="flex justify-between items-center gap-x-2 pt-6">
        <div>
          <h3 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">{title}</h3>
          <p className="text-sm text-xtnd-dark dark:text-xtnd-light">{description}</p>
        </div>
        <div className="flex items-center gap-4">
          {selectedCount > 0 && (
            <Button
              variant="primary"
              size="sm"
              className="min-h-[2.375rem]"
              leftIcon={<Icon name="FaPrint" />}
              onClick={() => {
                onPrintPdf?.();
              }}
              disabled={isPrinting}
              loading={isPrinting}
            >
              {isPrinting ? 'Generating PDF...' : `Print PDF (${selectedCount})`}
            </Button>
          )}
          <ArReportingReload isLoading={isLoading} refetch={onRefresh} />
        </div>
      </div>

      {/* Toolbar Row */}
      <div>{renderToolbar()}</div>
    </div>
  );
}
